import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import OverlayWindow from './OverlayWindow';
import ServiceLinksTab from './ServiceLinksTab';
import { debugAPI, lessonsAPI, operationsAPI } from '../services/api';
import { openLesson } from '../utils/lessonRoutes';
import { invalidateProgressQueries } from '../utils/progressQueries';
import buildInfo from '../buildInfo.json';
import { parseInstant } from '../utils/datetime';

const TAB_LINKS = 'links';
const TAB_OPERATIONS = 'operations';
const TAB_POOLS = 'pools';
const TAB_GOALS = 'goals';
const TAB_BUG_REPORTS = 'bug-reports';
const TAB_PROMOS = 'promos';
const TAB_LESSON = 'lesson';

const formatDateTime = (value) => {
  if (!value) return '-';
  const date = parseInstant(value);
  return date ? date.toLocaleString() : String(value);
};

// <input type="datetime-local"> speaks local wall-clock time, so an instant
// has to be shifted into the viewer's zone on the way in and back out again
// on the way to the API — otherwise every edit drifts by the UTC offset.
const toLocalInputValue = (value) => {
  const d = parseInstant(value);
  if (!d) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
    + `T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const fromLocalInputValue = (value) => {
  if (!value) return null;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
};

const formatDuration = (start, end) => {
  if (!start) return '-';
  const startDate = parseInstant(start);
  if (!startDate) return '-';
  const ms = (end ? parseInstant(end) : new Date()) - startDate;
  if (ms < 0) return '-';
  if (ms < 1000) return `${ms}ms`;
  if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
  return `${Math.floor(ms / 60000)}m ${Math.round((ms % 60000) / 1000)}s`;
};

const formatFailedTaskNames = (failedTaskNames) => {
  if (!failedTaskNames || failedTaskNames.length === 0) return '-';
  return failedTaskNames.join(', ');
};

const StatusBadge = ({ status, map }) => {
  const colors = map?.[status] ?? 'bg-[var(--cream)] text-[var(--ink-700)]';
  return (
    <span className={`inline-block px-1.5 py-0.5 text-[10px] font-semibold ${colors}`}>
      {status}
    </span>
  );
};

const OPERATION_STATUS_COLORS = {
  succeeded: 'bg-[var(--success)]/10 text-[var(--success)]',
  failed: 'bg-[var(--error)]/10 text-[var(--error)]',
  running: 'bg-[var(--der)]/10 text-[var(--der-text)]',
  cancelled: 'bg-[var(--ink-400)]/10 text-[var(--ink-500)]',
};

const TASK_STATUS_COLORS = {
  succeeded: 'bg-[var(--success)]/10 text-[var(--success)]',
  failed: 'bg-[var(--error)]/10 text-[var(--error)]',
  running: 'bg-[var(--der)]/10 text-[var(--der-text)]',
  queued: 'bg-[var(--die)]/10 text-[var(--die-text)]',
};

const GOAL_KEYS = ['words', 'genus', 'verb_forms', 'nvv', 'objects', 'reflexive', 'plural'];

const GoalHistoryTab = () => {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [adding, setAdding] = useState(false);
  const [addForm, setAddForm] = useState({ changed_at: '', goals: {} });

  const fetchHistory = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await debugAPI.getGoalHistory();
      setEntries(data);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchHistory(); }, [fetchHistory]);

  const startEdit = (entry) => {
    setEditingId(entry.id);
    setEditForm({
      changed_at: toLocalInputValue(entry.changed_at),
      goals: { ...entry.goals },
    });
  };

  const saveEdit = async () => {
    try {
      await debugAPI.updateGoalHistoryEntry(editingId, {
        changed_at: fromLocalInputValue(editForm.changed_at),
        goals: editForm.goals,
      });
      setEditingId(null);
      fetchHistory();
    } catch (e) {
      setError(e.message);
    }
  };

  const deleteEntry = async (id) => {
    try {
      await debugAPI.deleteGoalHistoryEntry(id);
      fetchHistory();
    } catch (e) {
      setError(e.message);
    }
  };

  const saveAdd = async () => {
    try {
      await debugAPI.createGoalHistoryEntry({
        changed_at: fromLocalInputValue(addForm.changed_at),
        goals: addForm.goals,
      });
      setAdding(false);
      setAddForm({ changed_at: '', goals: {} });
      fetchHistory();
    } catch (e) {
      setError(e.message);
    }
  };

  const GoalInputs = ({ goals, onChange }) => (
    <div className="flex flex-wrap gap-2">
      {GOAL_KEYS.map((k) => (
        <label key={k} className="flex items-center gap-1 text-[10px]">
          <span className="text-[var(--ink-500)] uppercase tracking-wide">{k}</span>
          <input
            type="number"
            min="0"
            className="w-12 px-1 py-0.5 border border-[var(--rule)] text-xs tabular-nums text-center"
            value={goals[k] ?? ''}
            onChange={(e) => {
              const v = e.target.value === '' ? undefined : Number(e.target.value);
              const next = { ...goals };
              if (v === undefined || v === 0) delete next[k]; else next[k] = v;
              onChange(next);
            }}
          />
        </label>
      ))}
    </div>
  );

  if (loading && entries.length === 0) {
    return <div className="text-xs text-[var(--ink-500)] py-4">Loading goal history...</div>;
  }

  return (
    <div className="space-y-3 text-xs">
      {error && (
        <div className="border border-[var(--error)] bg-red-50 px-3 py-2 text-[var(--error)]">{error}</div>
      )}

      <div className="flex items-center justify-between">
        <span className="text-[var(--ink-500)]">{entries.length} entries</span>
        <button
          onClick={() => { setAdding(true); setAddForm({ changed_at: toLocalInputValue(new Date()), goals: {} }); }}
          className="px-2 py-1 text-[10px] font-semibold bg-[var(--ink-900)] text-white hover:bg-[var(--ink-700)]"
        >+ Add</button>
      </div>

      {adding && (
        <div className="border border-[var(--der)] bg-blue-50 p-3 space-y-2">
          <label className="flex items-center gap-2">
            <span className="text-[var(--ink-500)] uppercase tracking-wide text-[10px] w-16">Date</span>
            <input
              type="datetime-local"
              className="px-2 py-1 border border-[var(--rule)] text-xs"
              value={addForm.changed_at}
              onChange={(e) => setAddForm((f) => ({ ...f, changed_at: e.target.value }))}
            />
          </label>
          <GoalInputs goals={addForm.goals} onChange={(g) => setAddForm((f) => ({ ...f, goals: g }))} />
          <div className="flex gap-2">
            <button onClick={saveAdd} className="px-2 py-1 text-[10px] font-semibold bg-[var(--success)] text-white">Save</button>
            <button onClick={() => setAdding(false)} className="px-2 py-1 text-[10px] text-[var(--ink-500)]">Cancel</button>
          </div>
        </div>
      )}

      <div className="overflow-x-auto">
        <table className="min-w-full text-xs">
          <thead>
            <tr className="border-b-2 border-[var(--ink-900)]">
              <th className="text-left py-1.5 pr-3 font-medium text-[10px] uppercase tracking-wide text-[var(--ink-500)]">Date</th>
              {GOAL_KEYS.map((k) => (
                <th key={k} className="text-center py-1.5 px-1 font-medium text-[10px] uppercase tracking-wide text-[var(--ink-500)]">{k}</th>
              ))}
              <th className="text-right py-1.5 pl-2 font-medium text-[10px] uppercase tracking-wide text-[var(--ink-500)]">Actions</th>
            </tr>
          </thead>
          <tbody>
            {entries.map((entry) => (
              <tr key={entry.id} className="border-b border-[var(--rule)]">
                {editingId === entry.id ? (
                  <>
                    <td className="py-1.5 pr-3">
                      <input
                        type="datetime-local"
                        className="px-1 py-0.5 border border-[var(--rule)] text-xs"
                        value={editForm.changed_at}
                        onChange={(e) => setEditForm((f) => ({ ...f, changed_at: e.target.value }))}
                      />
                    </td>
                    {GOAL_KEYS.map((k) => (
                      <td key={k} className="text-center py-1.5 px-1">
                        <input
                          type="number"
                          min="0"
                          className="w-10 px-1 py-0.5 border border-[var(--rule)] text-xs tabular-nums text-center"
                          value={editForm.goals[k] ?? ''}
                          onChange={(e) => {
                            const v = e.target.value === '' ? undefined : Number(e.target.value);
                            setEditForm((f) => {
                              const next = { ...f.goals };
                              if (v === undefined || v === 0) delete next[k]; else next[k] = v;
                              return { ...f, goals: next };
                            });
                          }}
                        />
                      </td>
                    ))}
                    <td className="text-right py-1.5 pl-2 whitespace-nowrap">
                      <button onClick={saveEdit} className="px-1.5 py-0.5 text-[10px] font-semibold bg-[var(--success)] text-white mr-1">Save</button>
                      <button onClick={() => setEditingId(null)} className="px-1.5 py-0.5 text-[10px] text-[var(--ink-500)]">Cancel</button>
                    </td>
                  </>
                ) : (
                  <>
                    <td className="py-1.5 pr-3 tabular-nums text-[var(--ink-700)] whitespace-nowrap">
                      {formatDateTime(entry.changed_at)}
                    </td>
                    {GOAL_KEYS.map((k) => (
                      <td key={k} className="text-center py-1.5 px-1 tabular-nums">
                        {entry.goals[k] != null ? (
                          <span className="font-semibold text-[var(--ink-900)]">{entry.goals[k]}</span>
                        ) : (
                          <span className="text-[var(--ink-400)]">–</span>
                        )}
                      </td>
                    ))}
                    <td className="text-right py-1.5 pl-2 whitespace-nowrap">
                      <button onClick={() => startEdit(entry)} className="px-1.5 py-0.5 text-[10px] text-[var(--der-text)] hover:underline mr-1">Edit</button>
                      <button onClick={() => deleteEntry(entry.id)} className="px-1.5 py-0.5 text-[10px] text-[var(--error)] hover:underline">Del</button>
                    </td>
                  </>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Promos debug tab — the user_promos table: one-time surfaced flows
// (e.g. the onboarding wizard). See backend/app/api/user.py /debug/promos.
// ---------------------------------------------------------------------------

const promoStatus = (promo) => {
  if (promo.completed_at) return { label: 'completed', colors: 'bg-[var(--success)]/10 text-[var(--success)]' };
  if (promo.dismissed_at) return { label: 'dismissed', colors: 'bg-[var(--die)]/10 text-[var(--die-text)]' };
  if (promo.seen) return { label: 'seen', colors: 'bg-[var(--der)]/10 text-[var(--der-text)]' };
  return { label: 'fresh', colors: 'bg-[var(--cream)] text-[var(--ink-700)]' };
};

// Lesson tab: regenerate a lesson — same words, name, type, source and
// started_at (so a plan_day lesson stays in today's plan), fresh questions;
// the old lesson is deleted. Defaults to the lesson open in the runner (read
// from the 'Lesson' section of window.__pageDebug); a row of the open-lessons
// list (GET /lessons: active + being_initialized) or a typed id picks another.
const LessonTab = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const [current, setCurrent] = useState(null);
  const [lessonId, setLessonId] = useState('');
  const [confirming, setConfirming] = useState(false);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);
  const [openLessons, setOpenLessons] = useState([]);
  const [listLoading, setListLoading] = useState(false);
  const [listError, setListError] = useState(null);

  const fetchOpenLessons = useCallback(async () => {
    setListLoading(true);
    setListError(null);
    try {
      const data = await lessonsAPI.getLessons();
      setOpenLessons(Array.isArray(data) ? data : []);
    } catch (e) {
      setListError(e.message);
    } finally {
      setListLoading(false);
    }
  }, []);

  useEffect(() => { fetchOpenLessons(); }, [fetchOpenLessons]);

  useEffect(() => {
    const poll = () => {
      const section = window.__pageDebug?.sections?.find((s) => s.name === 'Lesson');
      setCurrent(section?.data?.currentLessonDetails ?? null);
    };
    poll();
    const id = setInterval(poll, 500);
    return () => clearInterval(id);
  }, []);

  const targetId = lessonId.trim() || current?.id || '';
  const isCurrent = !!current?.id && targetId === current.id;

  const handleRegenerate = useCallback(async () => {
    if (!targetId) return;
    setRunning(true);
    setError(null);
    setResult(null);
    try {
      const res = await lessonsAPI.regenerateLesson(targetId);
      setResult(res);
      setConfirming(false);
      setLessonId('');
      invalidateProgressQueries(queryClient);
      fetchOpenLessons();
      // The runner showing the old lesson moves to the new one; elsewhere
      // the refetched lists pick it up.
      if (location.pathname.startsWith('/practice/') && isCurrent) {
        openLesson(navigate, {
          id: res.lesson_id,
          name: res.name,
          lesson_type: res.lesson_type,
          status: 'being_initialized',
          operation_id: res.operation_id,
          total_questions: res.total_questions,
        }, { replace: true });
      }
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally {
      setRunning(false);
    }
  }, [targetId, isCurrent, location.pathname, navigate, queryClient, fetchOpenLessons]);

  return (
    <div className="space-y-4 text-xs text-[var(--ink-700)]">
      <div>
        <div className="text-[11px] font-semibold uppercase tracking-wide text-[var(--ink-500)] mb-2">
          Current lesson
        </div>
        {current ? (
          <div className="border border-[var(--rule)] bg-[var(--cream)] px-3 py-2 space-y-1">
            <div><span className="text-[var(--ink-500)]">Name:</span> <span className="font-semibold">{current.name || '-'}</span></div>
            <div><span className="text-[var(--ink-500)]">Type:</span> <span className="font-mono">{current.lessonType || '-'}</span> · <span className="text-[var(--ink-500)]">Source:</span> <span className="font-mono">{current.source || '-'}</span></div>
            <div><span className="text-[var(--ink-500)]">Words:</span> {Array.isArray(current.wordIds) ? current.wordIds.length : '-'} · <span className="text-[var(--ink-500)]">Status:</span> <span className="font-mono">{current.status || '-'}</span></div>
            <div><span className="text-[var(--ink-500)]">Started:</span> {formatDateTime(current.startedAt)}</div>
            <div><span className="text-[var(--ink-500)]">ID:</span> <span className="font-mono text-[10px]">{current.id}</span></div>
          </div>
        ) : (
          <div className="border border-[var(--rule)] bg-[var(--cream)] px-3 py-2 text-[var(--ink-400)]">
            No lesson open — open one, or enter a lesson id below.
          </div>
        )}
      </div>

      <div>
        <div className="flex items-center justify-between mb-2">
          <div className="text-[11px] font-semibold uppercase tracking-wide text-[var(--ink-500)]">
            Open lessons {listLoading ? '' : `(${openLessons.length})`}
          </div>
          <button
            type="button"
            onClick={fetchOpenLessons}
            className="border border-[var(--rule)] px-2.5 py-1 text-xs font-medium text-[var(--ink-700)] hover:bg-[var(--cream)]"
          >
            {listLoading ? 'Loading…' : 'Refresh'}
          </button>
        </div>
        {listError && (
          <div className="mb-2 border border-[var(--error)] bg-red-50 px-3 py-2 text-[var(--error)]">{listError}</div>
        )}
        {!listLoading && !listError && openLessons.length === 0 ? (
          <div className="border border-[var(--rule)] bg-[var(--cream)] px-3 py-2 text-[var(--ink-400)]">
            No open lessons.
          </div>
        ) : (
          <ul className="border border-[var(--rule)] divide-y divide-[var(--rule)]" aria-label="Open lessons">
            {openLessons.map((l) => {
              const selected = l.id === targetId;
              const answered = (l.correct_answers || 0) + (l.incorrect_answers || 0);
              return (
                <li key={l.id}>
                  <button
                    type="button"
                    aria-pressed={selected}
                    onClick={() => { setLessonId(l.id === current?.id ? '' : l.id); setConfirming(false); }}
                    className={`w-full text-left px-3 py-2 flex items-baseline gap-3 ${
                      selected ? 'bg-[var(--ink-900)] text-white' : 'bg-white hover:bg-[var(--cream)]'
                    }`}
                  >
                    <span className="flex-1 min-w-0">
                      <span className="font-semibold">{l.name || '(unnamed)'}</span>
                      {l.id === current?.id && <span className="ml-1.5 text-[10px] opacity-70">· open now</span>}
                      <span className={`block font-mono text-[10px] ${selected ? 'opacity-80' : 'text-[var(--ink-500)]'}`}>
                        {l.lesson_type || '-'} · {l.source || '-'} · {l.status}
                      </span>
                    </span>
                    <span className={`shrink-0 text-[10px] text-right ${selected ? 'opacity-80' : 'text-[var(--ink-500)]'}`}>
                      {answered}/{l.total_questions ?? l.word_count ?? 0}
                      <span className="block">{formatDateTime(l.started_at)}</span>
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      <div>
        <div className="text-[11px] font-semibold uppercase tracking-wide text-[var(--ink-500)] mb-2">
          Regenerate lesson
        </div>
        <p className="text-[10px] text-[var(--ink-500)] mb-2">
          Creates a new lesson with the same words, name, type, source and start time
          (a planned lesson stays in today&apos;s plan), generates fresh questions and
          deletes the old lesson. Answered questions keep their FSRS history.
        </p>
        <div className="flex flex-wrap items-center gap-2">
          <input
            type="text"
            value={lessonId}
            onChange={(e) => { setLessonId(e.target.value); setConfirming(false); }}
            placeholder={current?.id || 'Lesson id'}
            aria-label="Lesson id"
            className="flex-1 min-w-[12rem] border border-[var(--rule)] bg-[var(--cream)] px-2 py-1.5 font-mono text-[11px] text-[var(--ink-900)] placeholder:text-[var(--ink-400)] focus:outline-none focus:border-[var(--ink-500)]"
          />
          {confirming ? (
            <>
              <button
                type="button"
                disabled={running}
                onClick={handleRegenerate}
                className="px-3 py-1.5 text-[11px] font-semibold bg-[var(--error)] text-white disabled:opacity-50"
              >
                {running ? 'Regenerating…' : 'Confirm regenerate'}
              </button>
              <button
                type="button"
                disabled={running}
                onClick={() => setConfirming(false)}
                className="px-2 py-1.5 text-[11px] text-[var(--ink-500)]"
              >
                Cancel
              </button>
            </>
          ) : (
            <button
              type="button"
              disabled={!targetId}
              onClick={() => setConfirming(true)}
              className="px-3 py-1.5 text-[11px] font-semibold bg-[var(--ink-900)] text-white hover:bg-[var(--ink-700)] disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Regenerate
            </button>
          )}
        </div>
        {error && (
          <div className="mt-2 border border-[var(--error)] bg-red-50 px-3 py-2 text-[var(--error)]">{String(error)}</div>
        )}
        {result && (
          <div className="mt-2 border border-[var(--rule)] bg-[var(--cream)] px-3 py-2">
            Regenerated → <span className="font-mono text-[10px]">{result.lesson_id}</span>
            {' '}({result.total_questions} question{result.total_questions === 1 ? '' : 's'})
          </div>
        )}
      </div>
    </div>
  );
};

const PromosTab = () => {
  const [promos, setPromos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [resetting, setResetting] = useState(null);
  const [confirmingKey, setConfirmingKey] = useState(null);
  const [expandedKey, setExpandedKey] = useState(null);

  const fetchPromos = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await debugAPI.listPromos();
      setPromos(Array.isArray(data) ? data : []);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchPromos(); }, [fetchPromos]);

  const handleReset = useCallback(async (promoKey) => {
    setResetting(promoKey);
    setError(null);
    try {
      await debugAPI.resetPromo(promoKey);
      setConfirmingKey(null);
      setExpandedKey(null);
      await fetchPromos();
    } catch (e) {
      setError(e.message);
    } finally {
      setResetting(null);
    }
  }, [fetchPromos]);

  return (
    <div className="space-y-3 text-xs text-[var(--ink-700)]">
      <div className="flex items-center justify-between">
        <span className="text-[var(--ink-500)]">
          {loading ? 'Loading…' : `${promos.length} promo${promos.length === 1 ? '' : 's'}`}
        </span>
        <button
          type="button"
          onClick={fetchPromos}
          className="border border-[var(--rule)] px-2.5 py-1 text-xs font-medium text-[var(--ink-700)] hover:bg-[var(--cream)]"
        >
          Refresh
        </button>
      </div>

      {error && (
        <div className="border border-[var(--error)] bg-[var(--error)]/10 px-3 py-2 text-[var(--error)]">{error}</div>
      )}

      <p className="text-[10px] text-[var(--ink-400)]">
        One-time surfaced flows (e.g. the onboarding wizard). A row is created lazily the
        first time a promo&apos;s status is read; resetting deletes the row, so the promo
        behaves as never surfaced and will auto-open again.
      </p>

      {!loading && promos.length === 0 ? (
        <div className="border border-[var(--rule)] bg-[var(--cream)] px-3 py-2 text-[var(--ink-400)]">
          No promo rows yet — none of the one-time flows has surfaced for this account.
        </div>
      ) : (
        <div className="overflow-x-auto border border-[var(--rule)]">
          <table className="min-w-full text-[11px]">
            <thead className="bg-[var(--cream)] text-[10px] uppercase tracking-wide text-[var(--ink-500)]">
              <tr>
                <th className="px-2 py-1.5 text-left font-semibold">Promo</th>
                <th className="px-2 py-1.5 text-left font-semibold">Status</th>
                <th className="px-2 py-1.5 text-left font-semibold">Seen</th>
                <th className="px-2 py-1.5 text-left font-semibold">Completed</th>
                <th className="px-2 py-1.5 text-left font-semibold">Dismissed</th>
                <th className="px-2 py-1.5 text-left font-semibold">Data</th>
                <th className="px-2 py-1.5 text-right font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 bg-white">
              {promos.map((promo) => {
                const status = promoStatus(promo);
                const hasData = promo.data && Object.keys(promo.data).length > 0;
                const isExpanded = expandedKey === promo.promo_key;
                const isConfirming = confirmingKey === promo.promo_key;
                const isResetting = resetting === promo.promo_key;
                return (
                  <React.Fragment key={promo.promo_key}>
                    <tr className="hover:bg-[var(--cream)]/60">
                      <td className="px-2 py-1.5 font-mono font-semibold text-[var(--ink-900)] whitespace-nowrap">
                        {promo.promo_key}
                      </td>
                      <td className="px-2 py-1.5">
                        <StatusBadge status={status.label} map={{ [status.label]: status.colors }} />
                      </td>
                      <td className="px-2 py-1.5 whitespace-nowrap text-[var(--ink-500)]">
                        {promo.seen ? formatDateTime(promo.seen_at) : '-'}
                      </td>
                      <td className="px-2 py-1.5 whitespace-nowrap text-[var(--ink-500)]">
                        {formatDateTime(promo.completed_at)}
                      </td>
                      <td className="px-2 py-1.5 whitespace-nowrap text-[var(--ink-500)]">
                        {formatDateTime(promo.dismissed_at)}
                      </td>
                      <td className="px-2 py-1.5">
                        {hasData ? (
                          <button
                            type="button"
                            onClick={() => setExpandedKey(isExpanded ? null : promo.promo_key)}
                            className="text-[var(--der-text)] hover:underline"
                          >
                            {isExpanded ? 'Hide' : 'Show'}
                          </button>
                        ) : (
                          <span className="text-[var(--ink-400)]">-</span>
                        )}
                      </td>
                      <td className="px-2 py-1.5 text-right whitespace-nowrap">
                        {isConfirming ? (
                          <>
                            <button
                              type="button"
                              disabled={isResetting}
                              onClick={() => handleReset(promo.promo_key)}
                              className="px-1.5 py-0.5 text-[10px] font-semibold bg-[var(--error)] text-white mr-1 disabled:opacity-50"
                            >
                              {isResetting ? 'Resetting…' : 'Confirm reset'}
                            </button>
                            <button
                              type="button"
                              disabled={isResetting}
                              onClick={() => setConfirmingKey(null)}
                              className="px-1.5 py-0.5 text-[10px] text-[var(--ink-500)]"
                            >
                              Cancel
                            </button>
                          </>
                        ) : (
                          <button
                            type="button"
                            onClick={() => setConfirmingKey(promo.promo_key)}
                            className="px-1.5 py-0.5 text-[10px] text-[var(--error)] hover:underline"
                          >
                            Reset
                          </button>
                        )}
                      </td>
                    </tr>
                    {isExpanded && hasData && (
                      <tr>
                        <td colSpan={7} className="px-2 py-2 bg-[var(--cream)]/80">
                          <pre className="border border-[var(--rule)] bg-white px-2 py-1.5 text-[10px] text-[var(--ink-700)] whitespace-pre-wrap break-words max-h-64 overflow-y-auto">
                            {JSON.stringify(promo.data, null, 2)}
                          </pre>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

const BUG_CATEGORIES = [
  { id: 'question_generation', label: 'Question generation', description: 'Wrong or nonsensical question was generated' },
  { id: 'rendering', label: 'Rendering', description: 'Question displayed incorrectly on screen' },
  { id: 'answer_validation', label: 'Answer validation', description: 'Correct answer marked wrong or vice versa' },
  { id: 'other', label: 'Other', description: 'Any other problem — describe it in the note below' },
];

const BugReportTab = () => {
  const [currentQ, setCurrentQ] = useState(null);
  const [reports, setReports] = useState([]);
  const [reportsLoading, setReportsLoading] = useState(false);
  const [submitting, setSubmitting] = useState(null);
  const [lastSubmitted, setLastSubmitted] = useState(null);
  const [error, setError] = useState(null);
  const [note, setNote] = useState('');

  useEffect(() => {
    const poll = () => {
      const pd = window.__pageDebug;
      if (!pd) { setCurrentQ(null); return; }
      const section = pd.sections?.find((s) => s.name === 'UnifiedDrillView');
      const q = section?.data?.currentQuestion ?? null;
      setCurrentQ(q);
    };
    poll();
    const id = setInterval(poll, 500);
    return () => clearInterval(id);
  }, []);

  const fetchReports = useCallback(async () => {
    setReportsLoading(true);
    setError(null);
    try {
      const data = await debugAPI.listBugReports();
      setReports(Array.isArray(data) ? data : []);
    } catch (e) {
      setError(e.message);
    } finally {
      setReportsLoading(false);
    }
  }, []);

  useEffect(() => { fetchReports(); }, [fetchReports]);

  const handleReport = useCallback(async (category) => {
    setSubmitting(category);
    setError(null);
    try {
      const versionInfo = await debugAPI.getVersionInfo().catch(() => null);
      await debugAPI.submitBugReport({
        category,
        note: note.trim() || null,
        question_id: currentQ?.id ?? null,
        question_text: currentQ?.questionText ?? null,
        question_type: currentQ?.questionType ?? null,
        word_id: currentQ?.wordId ?? null,
        word_german: currentQ?.wordGerman ?? null,
        frontend_version: buildInfo?.buildTime ?? null,
        backend_version: versionInfo?.backend?.short ?? null,
      });
      setLastSubmitted(category);
      setNote('');
      fetchReports();
      setTimeout(() => setLastSubmitted(null), 3000);
    } catch (e) {
      setError(e.message);
    } finally {
      setSubmitting(null);
    }
  }, [currentQ, fetchReports, note]);

  return (
    <div className="space-y-4 text-xs text-[var(--ink-700)]">
      <div>
        <div className="text-[11px] font-semibold uppercase tracking-wide text-[var(--ink-500)] mb-2">
          Current question context
        </div>
        {currentQ ? (
          <div className="border border-[var(--rule)] bg-[var(--cream)] px-3 py-2 space-y-1">
            <div><span className="text-[var(--ink-500)]">Word:</span> <span className="font-semibold">{currentQ.wordGerman ?? currentQ.word ?? '-'}</span>{currentQ.wordId ? <span className="text-[var(--ink-400)] ml-1">(#{currentQ.wordId})</span> : null}</div>
            <div><span className="text-[var(--ink-500)]">Type:</span> <span className="font-mono">{currentQ.questionType ?? '-'}</span></div>
            <div><span className="text-[var(--ink-500)]">Question:</span> <span className="italic">{currentQ.questionText ?? '-'}</span></div>
            <div><span className="text-[var(--ink-500)]">Question ID:</span> <span className="font-mono text-[10px]">{currentQ.id ?? '-'}</span></div>
          </div>
        ) : (
          <div className="border border-[var(--rule)] bg-[var(--cream)] px-3 py-2 text-[var(--ink-400)]">
            No active question — open a lesson to capture context.
          </div>
        )}
      </div>

      <div>
        <div className="text-[11px] font-semibold uppercase tracking-wide text-[var(--ink-500)] mb-2">
          Report a bug
        </div>
        {error && (
          <div className="border border-[var(--error)] bg-red-50 px-3 py-2 text-[var(--error)] mb-2">{error}</div>
        )}
        <div className="space-y-2">
          {BUG_CATEGORIES.map(({ id, label, description }) => {
            const isSubmitting = submitting === id;
            const wasSubmitted = lastSubmitted === id;
            return (
              <div key={id} className="flex items-center gap-3 border border-[var(--rule)] bg-[var(--cream)] px-3 py-2">
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-[var(--ink-900)]">{label}</div>
                  <div className="text-[10px] text-[var(--ink-500)]">{description}</div>
                </div>
                <button
                  type="button"
                  disabled={isSubmitting || !!submitting}
                  onClick={() => handleReport(id)}
                  className={`shrink-0 px-3 py-1.5 text-[11px] font-semibold transition-colors ${
                    wasSubmitted
                      ? 'bg-[var(--success)] text-white'
                      : 'bg-[var(--ink-900)] text-white hover:bg-[var(--ink-700)] disabled:opacity-50 disabled:cursor-not-allowed'
                  }`}
                >
                  {isSubmitting ? 'Sending…' : wasSubmitted ? 'Reported!' : 'Report'}
                </button>
              </div>
            );
          })}
        </div>
        <div className="mt-2">
          <label className="block text-[10px] font-semibold uppercase tracking-wide text-[var(--ink-500)] mb-1">
            Note (optional)
          </label>
          <textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Add any extra detail to attach to the report…"
            rows={2}
            className="w-full border border-[var(--rule)] bg-[var(--cream)] px-2 py-1.5 text-[11px] text-[var(--ink-900)] placeholder:text-[var(--ink-400)] resize-none focus:outline-none focus:border-[var(--ink-500)]"
          />
        </div>
      </div>

      <div>
        <div className="flex items-center justify-between mb-2">
          <div className="text-[11px] font-semibold uppercase tracking-wide text-[var(--ink-500)]">
            Recent reports ({reports.length})
          </div>
          <button
            type="button"
            onClick={fetchReports}
            className="border border-[var(--rule)] px-2 py-0.5 text-[10px] font-medium text-[var(--ink-700)] hover:bg-[var(--cream)]"
          >
            Refresh
          </button>
        </div>
        {reportsLoading && reports.length === 0 ? (
          <div className="text-[var(--ink-400)] py-2">Loading…</div>
        ) : reports.length === 0 ? (
          <div className="border border-[var(--rule)] bg-[var(--cream)] px-3 py-2 text-[var(--ink-400)]">No reports yet.</div>
        ) : (
          <div className="overflow-x-auto border border-[var(--rule)]">
            <table className="min-w-full text-[11px]">
              <thead className="bg-[var(--cream)] text-[10px] uppercase tracking-wide text-[var(--ink-500)]">
                <tr>
                  <th className="px-2 py-1.5 text-left font-semibold">When</th>
                  <th className="px-2 py-1.5 text-left font-semibold">Category</th>
                  <th className="px-2 py-1.5 text-left font-semibold">Note</th>
                  <th className="px-2 py-1.5 text-left font-semibold">Word</th>
                  <th className="px-2 py-1.5 text-left font-semibold">Type</th>
                  <th className="px-2 py-1.5 text-left font-semibold">Question</th>
                  <th className="px-2 py-1.5 text-left font-semibold">FE ver</th>
                  <th className="px-2 py-1.5 text-left font-semibold">BE ver</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 bg-white">
                {reports.map((r) => (
                  <tr key={r.id} className="hover:bg-[var(--cream)]/60">
                    <td className="px-2 py-1.5 whitespace-nowrap text-[var(--ink-500)]">{formatDateTime(r.reported_at)}</td>
                    <td className="px-2 py-1.5 whitespace-nowrap font-semibold text-[var(--ink-900)]">{r.category.replace(/_/g, ' ')}</td>
                    <td className="px-2 py-1.5 max-w-[160px] truncate text-[var(--ink-700)]" title={r.note ?? ''}>{r.note ?? '-'}</td>
                    <td className="px-2 py-1.5 whitespace-nowrap">{r.word_german ?? '-'}{r.word_id ? <span className="text-[var(--ink-400)] ml-1">(#{r.word_id})</span> : null}</td>
                    <td className="px-2 py-1.5 whitespace-nowrap font-mono text-[var(--ink-500)]">{r.question_type ?? '-'}</td>
                    <td className="px-2 py-1.5 max-w-[220px] truncate text-[var(--ink-700)]" title={r.question_text ?? ''}>{r.question_text ?? '-'}</td>
                    <td className="px-2 py-1.5 font-mono text-[var(--ink-500)]">{r.frontend_version ?? '-'}</td>
                    <td className="px-2 py-1.5 font-mono text-[var(--ink-500)]">{r.backend_version ?? '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

const DebugWindow = ({ isOpen, onClose }) => {
  const [activeTab, setActiveTab] = useState(TAB_LINKS);
  const [operations, setOperations] = useState([]);
  const [operationsLoading, setOperationsLoading] = useState(false);
  const [operationsError, setOperationsError] = useState(null);
  const [selectedOperationId, setSelectedOperationId] = useState(null);
  const [selectedOperation, setSelectedOperation] = useState(null);
  const [selectedOperationLoading, setSelectedOperationLoading] = useState(false);
  const [selectedOperationError, setSelectedOperationError] = useState(null);
  const [rqJobs, setRqJobs] = useState(null);
  const [rqJobsLoading, setRqJobsLoading] = useState(false);
  const [rqJobsError, setRqJobsError] = useState(null);
  const [expandedJobId, setExpandedJobId] = useState(null);
  const [opTasks, setOpTasks] = useState(null);
  const [opTasksLoading, setOpTasksLoading] = useState(false);
  const [opTasksError, setOpTasksError] = useState(null);
  const [expandedTaskId, setExpandedTaskId] = useState(null);
  const [poolInfo, setPoolInfo] = useState(null);
  const [poolLoading, setPoolLoading] = useState(false);
  const [poolError, setPoolError] = useState(null);
  const [selectedForecastDay, setSelectedForecastDay] = useState(null);
  const [versionInfo, setVersionInfo] = useState(null);

  const tabs = useMemo(() => ([
    { id: TAB_LINKS, label: 'Links' },
    { id: TAB_LESSON, label: 'Lesson' },
    { id: TAB_OPERATIONS, label: 'Operations' },
    { id: TAB_POOLS, label: 'Pools' },
    { id: TAB_GOALS, label: 'Goals' },
    { id: TAB_PROMOS, label: 'Promos' },
    { id: TAB_BUG_REPORTS, label: 'Bug Reports' },
  ]), []);

  const fetchOperations = useCallback(async () => {
    setOperationsLoading(true);
    setOperationsError(null);
    try {
      const data = await operationsAPI.list({ limit: 50, order: 'created_at_desc' });
      setOperations(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Failed to load operations:', err);
      setOperationsError('Unable to load operations.');
    } finally {
      setOperationsLoading(false);
    }
  }, []);

  const fetchRqJobs = useCallback(async (operationId) => {
    setRqJobsLoading(true);
    setRqJobsError(null);
    try {
      const data = await operationsAPI.getJobs(operationId);
      setRqJobs(data);
    } catch (err) {
      console.error('Failed to load RQ jobs:', err);
      setRqJobsError('Unable to load RQ job info.');
      setRqJobs(null);
    } finally {
      setRqJobsLoading(false);
    }
  }, []);

  const fetchOpTasks = useCallback(async (operationId) => {
    setOpTasksLoading(true);
    setOpTasksError(null);
    try {
      const data = await operationsAPI.getTasks(operationId);
      setOpTasks(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Failed to load operation tasks:', err);
      setOpTasksError('Unable to load task info.');
      setOpTasks(null);
    } finally {
      setOpTasksLoading(false);
    }
  }, []);

  const fetchOperationDetails = useCallback(async (operationId, fallback) => {
    setSelectedOperationId(operationId);
    setSelectedOperation(fallback || null);
    setSelectedOperationLoading(true);
    setSelectedOperationError(null);
    setRqJobs(null);
    setRqJobsError(null);
    setExpandedJobId(null);
    setOpTasks(null);
    setOpTasksError(null);
    setExpandedTaskId(null);
    try {
      const data = await operationsAPI.get(operationId);
      setSelectedOperation(data);
    } catch (err) {
      console.error('Failed to load operation details:', err);
      setSelectedOperationError('Unable to load operation details.');
      setSelectedOperation(fallback || null);
    } finally {
      setSelectedOperationLoading(false);
    }
    fetchOpTasks(operationId);
    fetchRqJobs(operationId);
  }, [fetchRqJobs, fetchOpTasks]);

  const refreshOperationDetails = useCallback(async () => {
    if (!selectedOperationId) return;
    setSelectedOperationLoading(true);
    setSelectedOperationError(null);
    try {
      const data = await operationsAPI.get(selectedOperationId);
      setSelectedOperation(data);
    } catch (err) {
      console.error('Failed to refresh operation details:', err);
      setSelectedOperationError('Unable to refresh operation details.');
    } finally {
      setSelectedOperationLoading(false);
    }
    fetchOpTasks(selectedOperationId);
    fetchRqJobs(selectedOperationId);
  }, [selectedOperationId, fetchRqJobs, fetchOpTasks]);

  const fetchPoolInfo = useCallback(async () => {
    setPoolLoading(true);
    setPoolError(null);
    try {
      const data = await debugAPI.getPoolInfo();
      setPoolInfo(data);
    } catch (err) {
      console.error('Failed to load pool info:', err);
      setPoolError('Unable to load pool info.');
    } finally {
      setPoolLoading(false);
    }
  }, []);

  const handleResetPlannedDate = useCallback(async () => {
    try {
      const result = await debugAPI.resetPlannedDate();
      setPoolInfo((prev) => prev ? { ...prev, last_planned_date: result.last_planned_date } : prev);
    } catch (err) {
      console.error('Failed to reset planned date:', err);
    }
  }, []);

  // Fetch version on open (shown in header)
  useEffect(() => {
    if (!isOpen || versionInfo) return;
    debugAPI.getVersionInfo().then(setVersionInfo).catch(() => null);
  }, [isOpen, versionInfo]);

  useEffect(() => {
    if (!isOpen) return;
    fetchOperations();
  }, [fetchOperations, isOpen]);

  useEffect(() => {
    if (!isOpen || activeTab !== TAB_POOLS) return;
    fetchPoolInfo();
  }, [activeTab, fetchPoolInfo, isOpen]);

  const renderOperationsTable = () => (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="text-xs text-[var(--ink-500)]">
          {operationsLoading ? 'Loading...' : `${operations.length} operations`}
        </div>
        <button
          type="button"
          onClick={fetchOperations}
          className="border border-[var(--rule)] px-2.5 py-1 text-xs font-medium text-[var(--ink-700)] hover:bg-[var(--cream)]"
        >
          Refresh
        </button>
      </div>
      {operationsError && (
        <div className="border border-[var(--error)] bg-[var(--error)]/10 px-3 py-2 text-xs text-[var(--error)]">
          {operationsError}
        </div>
      )}
      <div className="overflow-x-auto border border-[var(--rule)]">
        <table className="min-w-full divide-y divide-gray-200 text-xs">
          <thead className="bg-[var(--cream)] text-[var(--ink-700)]">
            <tr>
              <th className="px-3 py-2 text-left font-semibold">ID</th>
              <th className="px-3 py-2 text-left font-semibold">Name</th>
              <th className="px-3 py-2 text-left font-semibold">Status</th>
              <th className="px-3 py-2 text-left font-semibold">Progress</th>
              <th className="px-3 py-2 text-left font-semibold">Failed</th>
              <th className="px-3 py-2 text-left font-semibold">Current task</th>
              <th className="px-3 py-2 text-left font-semibold">Created</th>
              <th className="px-3 py-2 text-left font-semibold">Duration</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 bg-white">
            {operationsLoading && (
              <tr>
                <td className="px-3 py-4 text-center text-[var(--ink-500)]" colSpan={8}>
                  Loading...
                </td>
              </tr>
            )}
            {!operationsLoading && operations.length === 0 && (
              <tr>
                <td className="px-3 py-4 text-center text-[var(--ink-500)]" colSpan={8}>
                  No operations found.
                </td>
              </tr>
            )}
            {!operationsLoading && operations.map((operation) => (
              <tr
                key={operation.id}
                className="cursor-pointer hover:bg-[var(--cream)]/60"
                onClick={() => fetchOperationDetails(operation.id, operation)}
              >
                <td className="px-3 py-2 font-mono text-[11px] text-[var(--ink-500)]" title={operation.id}>
                  {operation.id.substring(0, 8)}…
                </td>
                <td className="px-3 py-2 text-[var(--ink-700)]">{operation.name}</td>
                <td className="px-3 py-2">
                  <StatusBadge status={operation.status} map={OPERATION_STATUS_COLORS} />
                </td>
                <td className="px-3 py-2 text-[var(--ink-700)] font-mono tabular-nums">
                  {operation.completed_tasks ?? 0}/{operation.total_tasks ?? 0}
                </td>
                <td className="px-3 py-2">
                  {(operation.failed_tasks ?? 0) > 0 ? (
                    <span className="text-[var(--error)] font-semibold">{operation.failed_tasks}</span>
                  ) : (
                    <span className="text-[var(--ink-400)]">0</span>
                  )}
                </td>
                <td className="px-3 py-2 text-[var(--ink-500)] max-w-[160px] truncate" title={operation.current_task_text || ''}>
                  {operation.current_task_text || '-'}
                </td>
                <td className="px-3 py-2 text-[var(--ink-500)] whitespace-nowrap">{formatDateTime(operation.created_at)}</td>
                <td className="px-3 py-2 text-[var(--ink-500)] whitespace-nowrap font-mono">
                  {formatDuration(operation.started_at, operation.completed_at)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="text-xs text-[var(--ink-500)]">Click a row to see detailed operation info.</p>
    </div>
  );

  const renderPoolInfo = () => (
    <div className="space-y-4 text-sm text-[var(--ink-700)]">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="text-xs text-[var(--ink-500)]">
          {poolLoading ? 'Loading...' : 'Pool status'}
        </div>
        <button
          type="button"
          onClick={fetchPoolInfo}
          className="border border-[var(--rule)] px-2.5 py-1 text-xs font-medium text-[var(--ink-700)] hover:bg-[var(--cream)]"
        >
          Refresh
        </button>
      </div>
      {poolError && (
        <div className="border border-[var(--error)] bg-[var(--error)]/10 px-3 py-2 text-xs text-[var(--error)]">
          {poolError}
        </div>
      )}
      {poolInfo && (
        <>
          {/* Pool counts */}
          <div>
            <div className="text-[11px] font-semibold uppercase tracking-wide text-[var(--ink-500)] mb-2">
              Pool Distribution
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
              {[
                { label: 'Active', value: poolInfo.pools?.active, color: 'text-[var(--success)] bg-[var(--success)]/10 border-[var(--success)]' },
                { label: 'Next', value: poolInfo.pools?.next, color: 'text-[var(--der-text)] bg-[var(--der)]/10 border-[var(--der)]' },
                { label: 'Suspended', value: poolInfo.pools?.suspended, color: 'text-[var(--die-text)] bg-[var(--die)]/5 border-[var(--die)]' },
                { label: 'Backlog', value: poolInfo.pools?.backlog, color: 'text-[var(--ink-700)] bg-[var(--cream)] border-[var(--rule)]' },
                { label: 'Total', value: poolInfo.pools?.total, color: 'text-[var(--ink-700)] bg-[var(--cream)] border-[var(--ink-500)]' },
              ].map((item) => (
                <div key={item.label} className={`border px-3 py-2 ${item.color}`}>
                  <div className="text-[10px] font-semibold uppercase tracking-wide opacity-70">{item.label}</div>
                  <div className="mt-1 text-lg font-bold">{item.value ?? 0}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Last planned date — shown before forecast since it determines the baseline */}
          <div className="flex flex-wrap items-center gap-3 py-1 border-b border-[var(--rule)] pb-3">
            <span className="text-[11px] font-semibold uppercase tracking-wide text-[var(--ink-500)]">Last planned:</span>
            <span className="border border-[var(--rule)] bg-[var(--cream)] px-2.5 py-1 text-xs text-[var(--ink-700)] font-mono">
              {poolInfo.last_planned_date || 'Never'}
            </span>
            <button
              type="button"
              onClick={handleResetPlannedDate}
              className="border border-[var(--die)] bg-[var(--die)]/10 px-2.5 py-1 text-xs font-medium text-[var(--die-text)] hover:bg-[var(--die)]/20"
            >
              Set back 1 day
            </button>
          </div>

          {/* 7-day forecast */}
          <div>
            <div className="text-[11px] font-semibold uppercase tracking-wide text-[var(--ink-500)] mb-2">
              7-Day Forecast (daily goal: {poolInfo.daily_goal})
            </div>
            {poolInfo.forecast && poolInfo.forecast.length > 0 ? (
              <>
                <div className="flex gap-2">
                  {poolInfo.forecast.map((count, i) => {
                    const pct = poolInfo.daily_goal > 0 ? Math.min(100, (count / poolInfo.daily_goal) * 100) : 0;
                    const atCap = count >= poolInfo.daily_goal;
                    const isSelected = selectedForecastDay === i;
                    return (
                      <div
                        key={i}
                        className={`flex-1 text-center cursor-pointer p-1 transition-colors ${isSelected ? 'bg-[var(--der)]/10 ring-1 ring-[var(--ink-900)]' : 'hover:bg-[var(--cream)]'}`}
                        onClick={() => setSelectedForecastDay(isSelected ? null : i)}
                      >
                        <div className="text-[10px] text-[var(--ink-500)] mb-1">
                          {i === 0 ? 'Today' : `+${i}d`}
                        </div>
                        <div className="relative h-16 bg-[var(--cream)] overflow-hidden">
                          <div
                            className={`absolute bottom-0 w-full transition-all ${atCap ? 'bg-[var(--error)]' : 'bg-[var(--der)]'}`}
                            style={{ height: `${pct}%` }}
                          />
                        </div>
                        <div className={`mt-1 text-xs font-semibold ${atCap ? 'text-[var(--error)]' : 'text-[var(--ink-700)]'}`}>
                          {count}
                        </div>
                        {(() => {
                          // Real load without the daily cap (due + overdue on
                          // day 0; deferred overflow on later days). Only shown
                          // when the cap is actually hiding something.
                          const raw = poolInfo.forecast_uncapped?.[i];
                          return raw !== undefined && raw !== count ? (
                            <div className="text-[10px] text-[var(--ink-500)] font-mono" title="Ohne Tageslimit">
                              ({raw})
                            </div>
                          ) : null;
                        })()}
                      </div>
                    );
                  })}
                </div>
                {selectedForecastDay !== null && (() => {
                  const dayWordIds = new Set(
                    (poolInfo.forecast_word_ids && poolInfo.forecast_word_ids[selectedForecastDay]) || []
                  );
                  const words = (poolInfo.active_schedule || []).filter((w) => dayWordIds.has(w.id));

                  return (
                    <div className="mt-2 border border-[var(--der)] bg-[var(--der)]/10 p-2">
                      <div className="text-[10px] font-semibold uppercase tracking-wide text-[var(--der-text)] mb-1">
                        {selectedForecastDay === 0 ? 'Due today' : `Due on +${selectedForecastDay}d`} ({words.length} words)
                      </div>
                      {words.length > 0 ? (
                        <div className="flex flex-wrap gap-1.5">
                          {words.map((w) => (
                            <span
                              key={w.id}
                              className={`inline-block px-1.5 py-0.5 text-[10px] font-medium border ${
                                w.state === 'mastered' ? 'bg-[var(--cream)] border-[var(--ink-500)] text-[var(--ink-700)]' :
                                w.state === 'review' ? 'bg-[var(--success)]/10 border-[var(--success)] text-[var(--success)]' :
                                w.state === 'learning' ? 'bg-[var(--der)]/10 border-[var(--der)] text-[var(--der-text)]' :
                                'bg-[var(--cream)] border-[var(--rule)] text-[var(--ink-700)]'
                              }`}
                              title={`stability: ${w.stability ?? '-'}, due: ${w.next_due || 'new'}`}
                            >
                              {w.german}
                            </span>
                          ))}
                        </div>
                      ) : (
                        <div className="text-[10px] text-[var(--ink-500)]">No words due on this day.</div>
                      )}
                    </div>
                  );
                })()}
              </>
            ) : (
              <div className="border border-[var(--rule)] bg-[var(--cream)] px-3 py-2 text-xs text-[var(--ink-500)]">
                No forecast data.
              </div>
            )}
          </div>

          {/* Plan day breakdown */}
          {poolInfo.plan_debug && (
            <div>
              <div className="text-[11px] font-semibold uppercase tracking-wide text-[var(--ink-500)] mb-2">
                Plan Day Breakdown
              </div>
              <div className="overflow-x-auto border border-[var(--rule)]">
                <table className="min-w-full divide-y divide-gray-200 text-[11px]">
                  <thead className="bg-[var(--cream)] text-[var(--ink-500)]">
                    <tr>
                      <th className="px-2 py-1.5 text-left font-semibold">Goal</th>
                      <th className="px-2 py-1.5 text-left font-semibold">Target</th>
                      <th className="px-2 py-1.5 text-left font-semibold">Due</th>
                      <th className="px-2 py-1.5 text-left font-semibold">New</th>
                      <th className="px-2 py-1.5 text-left font-semibold" title="Vorgezogen: noch nicht fällige Wörter, die das Wörter-Ziel auffüllen">Ahead</th>
                      <th className="px-2 py-1.5 text-left font-semibold">Unsupported</th>
                      <th className="px-2 py-1.5 text-left font-semibold">Planned</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 bg-white">
                    {Object.entries(poolInfo.plan_debug).map(([key, info]) => (
                      <tr key={key} className={info.total_planned < info.target ? 'bg-[var(--die)]/5' : ''}>
                        <td className="px-2 py-1.5 text-[var(--ink-700)] font-medium">{key}</td>
                        <td className="px-2 py-1.5 text-[var(--ink-500)] font-mono">{info.target}</td>
                        <td className="px-2 py-1.5 text-[var(--ink-500)] font-mono">{info.due}</td>
                        <td className="px-2 py-1.5 text-[var(--ink-500)] font-mono">{info.new}</td>
                        <td className="px-2 py-1.5 text-[var(--ink-500)] font-mono">{info.ahead ?? 0}</td>
                        <td className="px-2 py-1.5 text-[var(--ink-500)] font-mono">{info.unsupported}</td>
                        <td className={`px-2 py-1.5 font-mono font-semibold ${info.total_planned < info.target ? 'text-[var(--die-text)]' : 'text-[var(--success)]'}`}>
                          {info.total_planned}/{info.target}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Active pool schedule */}
          <div>
            <div className="text-[11px] font-semibold uppercase tracking-wide text-[var(--ink-500)] mb-2">
              Active Pool ({poolInfo.active_schedule?.length ?? 0} words)
            </div>
            {poolInfo.active_schedule && poolInfo.active_schedule.length > 0 ? (
              <div className="overflow-x-auto border border-[var(--rule)] max-h-64 overflow-y-auto">
                <table className="min-w-full divide-y divide-gray-200 text-[11px]">
                  <thead className="bg-[var(--cream)] text-[var(--ink-500)] sticky top-0">
                    <tr>
                      <th className="px-2 py-1.5 text-left font-semibold">Word</th>
                      <th className="px-2 py-1.5 text-left font-semibold">State</th>
                      <th className="px-2 py-1.5 text-left font-semibold">Stability</th>
                      <th className="px-2 py-1.5 text-left font-semibold">Difficulty</th>
                      <th className="px-2 py-1.5 text-left font-semibold">Next Due</th>
                      <th className="px-2 py-1.5 text-left font-semibold">Last Review</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 bg-white">
                    {poolInfo.active_schedule.map((w) => {
                      const due = parseInstant(w.next_due);
                      const isDue = due !== null && due <= new Date();
                      return (
                        <tr key={w.id} className={isDue ? 'bg-[var(--die)]/5' : ''}>
                          <td className="px-2 py-1.5 text-[var(--ink-700)] font-medium">{w.german}</td>
                          <td className="px-2 py-1.5">
                            <span className={`inline-block px-1.5 py-0.5 text-[10px] font-semibold ${
                              w.state === 'mastered' ? 'bg-[var(--cream)] text-[var(--ink-700)]' :
                              w.state === 'review' ? 'bg-[var(--success)]/10 text-[var(--success)]' :
                              w.state === 'learning' ? 'bg-[var(--der)]/10 text-[var(--der-text)]' :
                              'bg-[var(--cream)] text-[var(--ink-700)]'
                            }`}>
                              {w.state}
                            </span>
                          </td>
                          <td className="px-2 py-1.5 text-[var(--ink-500)] font-mono">{w.stability ?? '-'}</td>
                          <td className="px-2 py-1.5 text-[var(--ink-500)] font-mono">{w.difficulty ?? '-'}</td>
                          <td className={`px-2 py-1.5 font-mono whitespace-nowrap ${isDue ? 'text-[var(--die-text)] font-semibold' : 'text-[var(--ink-500)]'}`}>
                            {w.next_due ? formatDateTime(w.next_due) : 'New'}
                          </td>
                          <td className="px-2 py-1.5 text-[var(--ink-500)] whitespace-nowrap">{formatDateTime(w.last_review)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="border border-[var(--rule)] bg-[var(--cream)] px-3 py-2 text-xs text-[var(--ink-500)]">
                No active pool words.
              </div>
            )}
          </div>

          {/* Recall ratings cache */}
          <div>
            <div className="text-[11px] font-semibold uppercase tracking-wide text-[var(--ink-500)] mb-2">
              Recall Ratings Cache
            </div>
            {poolInfo.recall_ratings_cache ? (
              <div className="space-y-2">
                <div className="flex gap-3 text-[11px] text-[var(--ink-500)]">
                  <span>Cached date: <span className="font-mono text-[var(--ink-700)]">{poolInfo.recall_ratings_cache.date}</span></span>
                  <span>First review: <span className="font-mono text-[var(--ink-700)]">{poolInfo.recall_ratings_cache.first_review}</span> (n={poolInfo.recall_ratings_cache.first_review_n})</span>
                </div>
                {poolInfo.recall_ratings_cache.bins && (
                  <div className="overflow-x-auto border border-[var(--rule)]">
                    <table className="min-w-full divide-y divide-gray-200 text-[11px]">
                      <thead className="bg-[var(--cream)] text-[var(--ink-500)]">
                        <tr>
                          <th className="px-2 py-1.5 text-left font-semibold">Recall Bin</th>
                          <th className="px-2 py-1.5 text-left font-semibold">Mode Rating</th>
                          <th className="px-2 py-1.5 text-left font-semibold">Samples</th>
                          <th className="px-2 py-1.5 text-left font-semibold">Distribution</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100 bg-white">
                        {poolInfo.recall_ratings_cache.bins.map((bin, i) => (
                          <tr key={i}>
                            <td className="px-2 py-1.5 text-[var(--ink-700)] font-mono">{bin.lo.toFixed(1)}–{bin.hi.toFixed(1)}</td>
                            <td className="px-2 py-1.5">
                              <span className={`inline-block px-1.5 py-0.5 text-[10px] font-semibold ${
                                bin.rating === 'AGAIN' ? 'bg-[var(--error)]/10 text-[var(--error)]' :
                                bin.rating === 'HARD' ? 'bg-[var(--die)]/10 text-[var(--die-text)]' :
                                bin.rating === 'GOOD' ? 'bg-[var(--success)]/10 text-[var(--success)]' :
                                'bg-[var(--der)]/10 text-[var(--der-text)]'
                              }`}>
                                {bin.rating}
                              </span>
                            </td>
                            <td className="px-2 py-1.5 text-[var(--ink-500)]">{bin.n}</td>
                            <td className="px-2 py-1.5 text-[var(--ink-500)] font-mono text-[10px]">
                              {Object.entries(bin.dist || {}).map(([k, v]) => `${k}:${v}`).join(' ')}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            ) : (
              <div className="border border-[var(--rule)] bg-[var(--cream)] px-3 py-2 text-xs text-[var(--ink-500)]">
                No recall ratings cache.
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );

  const detailEntries = useMemo(() => {
    if (!selectedOperation) return [];
    return [
      { label: 'ID', value: selectedOperation.id },
      { label: 'Name', value: selectedOperation.name },
      { label: 'Type', value: selectedOperation.operation_type || '-' },
      { label: 'Phase gate', value: selectedOperation.phase_gate || '-' },
      { label: 'Status', value: selectedOperation.status },
      { label: 'Progress', value: `${selectedOperation.completed_tasks ?? 0}/${selectedOperation.total_tasks ?? 0}` },
      { label: 'Failed tasks', value: selectedOperation.failed_tasks ?? 0 },
      { label: 'Failed task names', value: formatFailedTaskNames(selectedOperation.failed_task_names) },
      { label: 'Current task', value: selectedOperation.current_task_text || '-' },
      { label: 'Created at', value: formatDateTime(selectedOperation.created_at) },
      { label: 'Started at', value: formatDateTime(selectedOperation.started_at) },
      { label: 'Completed at', value: formatDateTime(selectedOperation.completed_at) },
      { label: 'Duration', value: formatDuration(selectedOperation.started_at, selectedOperation.completed_at) },
    ];
  }, [selectedOperation]);

  const closeDetailWindow = () => {
    setSelectedOperationId(null);
    setSelectedOperation(null);
    setSelectedOperationError(null);
    setSelectedOperationLoading(false);
    setRqJobs(null);
    setRqJobsError(null);
    setOpTasks(null);
    setOpTasksError(null);
    setExpandedTaskId(null);
  };

  return (
    <>
      <OverlayWindow
        isOpen={isOpen}
        onClose={onClose}
        name="Debug panel"
        closeOnBackdrop={true}
        panelClassName="relative w-full max-w-6xl max-h-[95vh] overflow-y-auto bg-white border-2 border-[var(--ink-900)]"
      >
        <div className="p-4 sm:p-6">
          <div className="space-y-4">
            {/* Header with version info */}
            <div className="flex items-start justify-between gap-4">
              <div>
                <h2 className="text-lg font-semibold text-[var(--ink-900)]">Debug</h2>
                <p className="text-xs text-[var(--ink-500)]">Ctrl+D · Runtime diagnostics</p>
              </div>
              <div className="text-right shrink-0 flex flex-col items-end gap-0.5">
                <span className="font-mono text-[10px] text-[var(--ink-400)]">
                  FE: {buildInfo.buildTime ? formatDateTime(buildInfo.buildTime) : 'dev'}
                </span>
                {versionInfo?.backend?.build_time && (
                  <span className="font-mono text-[10px] text-[var(--ink-400)]">
                    BE: {formatDateTime(versionInfo.backend.build_time)}
                  </span>
                )}
              </div>
            </div>

            {/* Tabs */}
            <div className="flex flex-wrap gap-1.5 border-b border-[var(--rule)] pb-2">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  type="button"
                  onClick={() => setActiveTab(tab.id)}
                  className={`px-3 py-1 text-xs font-semibold transition-colors inline-flex items-center gap-1.5 ${
                    activeTab === tab.id
                      ? 'bg-[var(--ink-900)] text-white'
                      : 'text-[var(--ink-500)] hover:bg-[var(--cream)] border border-transparent hover:border-[var(--rule)]'
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            {/* Tab content */}
            <div>
              {activeTab === TAB_LINKS && <ServiceLinksTab />}
              {activeTab === TAB_LESSON && <LessonTab />}
              {activeTab === TAB_OPERATIONS && renderOperationsTable()}
              {activeTab === TAB_POOLS && renderPoolInfo()}
              {activeTab === TAB_GOALS && <GoalHistoryTab />}
              {activeTab === TAB_PROMOS && <PromosTab />}
              {activeTab === TAB_BUG_REPORTS && <BugReportTab />}
            </div>
          </div>
        </div>
      </OverlayWindow>

      {/* Operation detail overlay */}
      <OverlayWindow
        isOpen={!!selectedOperationId}
        onClose={closeDetailWindow}
        name="Operation details"
        closeOnBackdrop={true}
        panelClassName="relative w-full max-w-4xl max-h-[90dvh] overflow-y-auto bg-white border-2 border-[var(--ink-900)]"
      >
        <div className="p-4 sm:p-6 space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-base font-semibold text-[var(--ink-900)]">Operation details</h3>
              {selectedOperation && (
                <p className="text-xs text-[var(--ink-500)] font-mono mt-0.5">{selectedOperation.id}</p>
              )}
            </div>
            <button
              type="button"
              onClick={refreshOperationDetails}
              disabled={selectedOperationLoading}
              className="border border-[var(--rule)] px-2.5 py-1 text-xs font-medium text-[var(--ink-700)] hover:bg-[var(--cream)] disabled:opacity-50"
            >
              {selectedOperationLoading ? 'Loading...' : 'Refresh'}
            </button>
          </div>
          {selectedOperationLoading && (
            <div className="border border-[var(--der)] bg-[var(--der)]/10 px-3 py-2 text-xs text-[var(--der-text)]">
              Loading operation details...
            </div>
          )}
          {selectedOperationError && (
            <div className="border border-[var(--error)] bg-[var(--error)]/10 px-3 py-2 text-xs text-[var(--error)]">
              {selectedOperationError}
            </div>
          )}
          {selectedOperation && (
            <div className="space-y-4 text-sm text-[var(--ink-700)]">
              <div className="grid gap-2 sm:grid-cols-2">
                {detailEntries.map((entry) => (
                  <div key={entry.label} className="border border-[var(--rule)] bg-[var(--cream)] px-3 py-2">
                    <div className="text-[11px] font-semibold uppercase tracking-wide text-[var(--ink-500)]">
                      {entry.label}
                    </div>
                    <div className="mt-1 text-xs text-[var(--ink-700)] break-words">{entry.value}</div>
                  </div>
                ))}
              </div>

              {/* Status override */}
              <div className="flex items-center gap-3">
                <div className="text-[11px] font-semibold uppercase tracking-wide text-[var(--ink-500)]">
                  Override status
                </div>
                <select
                  value={selectedOperation.status}
                  onChange={async (e) => {
                    const newStatus = e.target.value;
                    try {
                      await operationsAPI.updateStatus(selectedOperation.id, newStatus);
                      setSelectedOperation((prev) => ({ ...prev, status: newStatus }));
                      fetchOperations();
                    } catch (err) {
                      console.error('Failed to update status:', err);
                    }
                  }}
                  className="border border-[var(--rule)] bg-white px-2 py-1 text-xs font-medium text-[var(--ink-700)] focus:outline-none focus:ring-1 focus:ring-[var(--ink-900)] focus:border-[var(--ink-900)]"
                >
                  {['pending', 'running', 'succeeded', 'failed', 'cancelled'].map((s) => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
              </div>

              {/* Operation Tasks */}
              <div>
                <div className="text-[11px] font-semibold uppercase tracking-wide text-[var(--ink-500)] mb-2">
                  Tasks
                </div>
                {opTasksError && (
                  <div className="border border-[var(--error)] bg-[var(--error)]/10 px-3 py-2 text-xs text-[var(--error)] mb-2">
                    {opTasksError}
                  </div>
                )}
                {opTasksLoading && !opTasks && (
                  <div className="border border-[var(--der)] bg-[var(--der)]/10 px-3 py-2 text-xs text-[var(--der-text)] mb-2">
                    Loading tasks...
                  </div>
                )}
                {opTasks && opTasks.length === 0 && (
                  <div className="border border-[var(--rule)] bg-[var(--cream)] px-3 py-2 text-xs text-[var(--ink-500)]">
                    No task rows found (legacy operation without task tracking).
                  </div>
                )}
                {opTasks && opTasks.length > 0 && (() => {
                  const phases = [...new Set(opTasks.map((t) => t.phase))].sort((a, b) => a - b);
                  return (
                    <div className="space-y-3">
                      {phases.map((phase) => {
                        const phaseTasks = opTasks.filter((t) => t.phase === phase);
                        const succeeded = phaseTasks.filter((t) => t.status === 'succeeded').length;
                        const failed = phaseTasks.filter((t) => t.status === 'failed').length;
                        return (
                          <div key={phase}>
                            <div className="flex items-center gap-2 mb-1">
                              <span className="text-[10px] font-semibold uppercase tracking-wide text-[var(--ink-500)]">
                                Phase {phase}
                              </span>
                              <span className="text-[10px] text-[var(--ink-500)]">
                                {succeeded}/{phaseTasks.length} done
                                {failed > 0 && <span className="text-[var(--error)] ml-1">{failed} failed</span>}
                              </span>
                            </div>
                            <div className="overflow-x-auto border border-[var(--rule)]">
                              <table className="min-w-full divide-y divide-gray-200 text-[11px]">
                                <thead className="bg-[var(--cream)] text-[var(--ink-500)]">
                                  <tr>
                                    <th className="px-2 py-1.5 text-left font-semibold">Type</th>
                                    <th className="px-2 py-1.5 text-left font-semibold">Status</th>
                                    <th className="px-2 py-1.5 text-left font-semibold">Task text</th>
                                    <th className="px-2 py-1.5 text-left font-semibold">Created</th>
                                    <th className="px-2 py-1.5 text-left font-semibold">Duration</th>
                                    <th className="px-2 py-1.5 text-left font-semibold">Retries</th>
                                  </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-100 bg-white">
                                  {phaseTasks.map((task) => (
                                    <React.Fragment key={task.id}>
                                      <tr
                                        className={`${task.error_message ? 'cursor-pointer bg-[var(--error)]/5 hover:bg-[var(--error)]/10' : ''} ${expandedTaskId === task.id ? 'bg-[var(--cream)]' : ''}`}
                                        onClick={task.error_message
                                          ? () => setExpandedTaskId(expandedTaskId === task.id ? null : task.id)
                                          : undefined}
                                      >
                                        <td className="px-2 py-1.5 text-[var(--ink-700)] font-mono whitespace-nowrap">
                                          {task.error_message && (
                                            <span className="text-[var(--error)] mr-1">{expandedTaskId === task.id ? '▾' : '▸'}</span>
                                          )}
                                          {task.task_type}
                                        </td>
                                        <td className="px-2 py-1.5">
                                          <StatusBadge status={task.status} map={TASK_STATUS_COLORS} />
                                        </td>
                                        <td className="px-2 py-1.5 text-[var(--ink-700)] max-w-[200px] truncate" title={task.current_task_text || ''}>
                                          {task.current_task_text || '-'}
                                        </td>
                                        <td className="px-2 py-1.5 text-[var(--ink-500)] whitespace-nowrap">{formatDateTime(task.created_at)}</td>
                                        <td className="px-2 py-1.5 text-[var(--ink-500)] font-mono whitespace-nowrap">
                                          {formatDuration(task.started_at, task.completed_at)}
                                        </td>
                                        <td className="px-2 py-1.5 text-[var(--ink-500)]">{task.retry_count ?? 0}</td>
                                      </tr>
                                      {task.error_message && expandedTaskId === task.id && (
                                        <tr>
                                          <td colSpan={6} className="px-2 py-2 bg-[var(--cream)]/80">
                                            <div className="text-[10px] font-semibold uppercase tracking-wide text-[var(--error)] mb-1">
                                              Error
                                            </div>
                                            <pre className="border border-[var(--error)] bg-[var(--error)]/10 px-2 py-1.5 text-[10px] text-[var(--error)] whitespace-pre-wrap break-words max-h-48 overflow-y-auto">
                                              {task.error_message}
                                            </pre>
                                          </td>
                                        </tr>
                                      )}
                                    </React.Fragment>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  );
                })()}
              </div>

              {/* RQ Jobs */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <div className="text-[11px] font-semibold uppercase tracking-wide text-[var(--ink-500)]">
                    Redis Queue Jobs
                  </div>
                  <button
                    type="button"
                    onClick={refreshOperationDetails}
                    disabled={rqJobsLoading || selectedOperationLoading}
                    className="border border-[var(--rule)] px-2 py-0.5 text-[11px] font-medium text-[var(--ink-700)] hover:bg-[var(--cream)] disabled:opacity-50"
                  >
                    {rqJobsLoading ? 'Loading...' : 'Refresh'}
                  </button>
                </div>
                {rqJobsError && (
                  <div className="border border-[var(--error)] bg-[var(--error)]/10 px-3 py-2 text-xs text-[var(--error)] mb-2">
                    {rqJobsError}
                  </div>
                )}
                {rqJobs && (
                  <div className="space-y-2">
                    <div className="flex gap-3 text-[11px] text-[var(--ink-500)]">
                      <span>Queue: <span className="font-mono text-[var(--ink-700)]">{rqJobs.queue_name}</span></span>
                      <span>Scanned: {rqJobs.total_jobs_scanned}</span>
                      <span>Matched: {rqJobs.matched_jobs}</span>
                    </div>
                    {rqJobs.jobs.length === 0 ? (
                      <div className="border border-[var(--rule)] bg-[var(--cream)] px-3 py-2 text-xs text-[var(--ink-500)]">
                        No RQ jobs found for this operation (jobs may have expired from Redis).
                      </div>
                    ) : (
                      <div className="overflow-x-auto border border-[var(--rule)]">
                        <table className="min-w-full divide-y divide-gray-200 text-[11px]">
                          <thead className="bg-[var(--cream)] text-[var(--ink-500)]">
                            <tr>
                              <th className="px-2 py-1.5 text-left font-semibold">Job ID</th>
                              <th className="px-2 py-1.5 text-left font-semibold">Status</th>
                              <th className="px-2 py-1.5 text-left font-semibold">Registry</th>
                              <th className="px-2 py-1.5 text-left font-semibold">Task</th>
                              <th className="px-2 py-1.5 text-left font-semibold">Enqueued</th>
                              <th className="px-2 py-1.5 text-left font-semibold">Duration</th>
                              <th className="px-2 py-1.5 text-left font-semibold">Retries left</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-100 bg-white">
                            {rqJobs.jobs.map((job) => (
                              <React.Fragment key={job.job_id}>
                                <tr
                                  className={`cursor-pointer hover:bg-[var(--cream)]/60 ${job.exc_info ? 'bg-[var(--error)]/5' : ''} ${expandedJobId === job.job_id ? 'bg-[var(--cream)]' : ''}`}
                                  onClick={() => setExpandedJobId(expandedJobId === job.job_id ? null : job.job_id)}
                                >
                                  <td className="px-2 py-1.5 font-mono text-[var(--ink-700)] whitespace-nowrap" title={job.job_id}>
                                    <span className="text-[var(--ink-400)] mr-1">{expandedJobId === job.job_id ? '▾' : '▸'}</span>
                                    {job.job_id.substring(0, 8)}…
                                  </td>
                                  <td className="px-2 py-1.5">
                                    <StatusBadge status={job.status} map={{
                                      finished: 'bg-[var(--success)]/10 text-[var(--success)]',
                                      failed: 'bg-[var(--error)]/10 text-[var(--error)]',
                                      started: 'bg-[var(--der)]/10 text-[var(--der-text)]',
                                      queued: 'bg-[var(--die)]/10 text-[var(--die-text)]',
                                    }} />
                                  </td>
                                  <td className="px-2 py-1.5 text-[var(--ink-500)]">{job.registry}</td>
                                  <td className="px-2 py-1.5 text-[var(--ink-700)] max-w-[200px] truncate" title={job.task_text || job.func_name}>
                                    {job.task_text || job.func_name.split('.').pop() || '-'}
                                  </td>
                                  <td className="px-2 py-1.5 text-[var(--ink-500)] whitespace-nowrap">{job.enqueued_at ? formatDateTime(job.enqueued_at) : '-'}</td>
                                  <td className="px-2 py-1.5 text-[var(--ink-500)] whitespace-nowrap font-mono">
                                    {formatDuration(job.started_at, job.ended_at)}
                                  </td>
                                  <td className="px-2 py-1.5 text-[var(--ink-500)]">{job.retries_left ?? '-'}</td>
                                </tr>
                                {expandedJobId === job.job_id && (
                                  <tr>
                                    <td colSpan={7} className="px-2 py-2 bg-[var(--cream)]/80">
                                      <div className="grid grid-cols-2 gap-x-4 gap-y-1.5 text-[11px] mb-2">
                                        <div><span className="text-[var(--ink-500)] font-semibold">Job ID:</span> <span className="font-mono text-[var(--ink-700)]">{job.job_id}</span></div>
                                        <div><span className="text-[var(--ink-500)] font-semibold">Function:</span> <span className="font-mono text-[var(--ink-700)]">{job.func_name}</span></div>
                                        <div><span className="text-[var(--ink-500)] font-semibold">Origin:</span> <span className="text-[var(--ink-700)]">{job.origin || '-'}</span></div>
                                        <div><span className="text-[var(--ink-500)] font-semibold">Timeout:</span> <span className="text-[var(--ink-700)]">{job.timeout ?? '-'}</span></div>
                                        <div><span className="text-[var(--ink-500)] font-semibold">Description:</span> <span className="text-[var(--ink-700)]">{job.description || '-'}</span></div>
                                        <div><span className="text-[var(--ink-500)] font-semibold">Retries left:</span> <span className="text-[var(--ink-700)]">{job.retries_left ?? '-'}</span></div>
                                      </div>
                                      {job.meta && Object.keys(job.meta).length > 0 && (
                                        <div className="mb-2">
                                          <div className="text-[10px] font-semibold uppercase tracking-wide text-[var(--ink-500)] mb-1">Meta</div>
                                          <div className="border border-[var(--rule)] bg-white px-2 py-1.5">
                                            {Object.entries(job.meta).map(([k, v]) => (
                                              <div key={k} className="text-[11px]">
                                                <span className="text-[var(--ink-500)]">{k}:</span>{' '}
                                                <span className="font-mono text-[var(--ink-700)]">{v}</span>
                                              </div>
                                            ))}
                                          </div>
                                        </div>
                                      )}
                                      {job.exc_info && (
                                        <div>
                                          <div className="text-[10px] font-semibold uppercase tracking-wide text-[var(--error)] mb-1">Exception</div>
                                          <pre className="border border-[var(--error)] bg-[var(--error)]/10 px-2 py-1.5 text-[10px] text-[var(--error)] whitespace-pre-wrap break-words max-h-48 overflow-y-auto">
                                            {job.exc_info}
                                          </pre>
                                        </div>
                                      )}
                                    </td>
                                  </tr>
                                )}
                              </React.Fragment>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                )}
              </div>

              <details>
                <summary className="text-[11px] font-semibold uppercase tracking-wide text-[var(--ink-500)] cursor-pointer">
                  Raw payload
                </summary>
                <pre className="mt-2 overflow-x-auto border border-[var(--rule)] bg-[var(--cream)] p-3 text-xs text-[var(--ink-700)]">
                  {JSON.stringify(selectedOperation, null, 2)}
                </pre>
              </details>
            </div>
          )}
        </div>
      </OverlayWindow>
    </>
  );
};

export default DebugWindow;
